import {
    ColumnDef, ColumnFiltersState, OnChangeFn, flexRender, getCoreRowModel, getFilteredRowModel, useReactTable
} from "@tanstack/react-table";
import { Box, Spinner, Text, useColorMode } from "theme-ui";
import { TabelClassname } from "../../utils/Classname";
import FilterPopup from "../forms-component/filterPopup";
import CommentPopup from "../pages/demotable/CommentPopup";
import TablePagination from "./Pagination";
import theme from "../../theme";
interface DataTableProps<T extends object> {
    data: T[];
    columns: ColumnDef<T>[];
    className?: string | undefined;
    onPageChange: (page: number | any) => void,
    onPerPageChange: (value: string) => void,
    currentPage?: any,
    TotalCount?: number;
    TotalLength?: any;
    perPageCount: number,
    showNavigation?: boolean;
    enableColumnFilters?: boolean,
    onColumnFiltersChange?: OnChangeFn<ColumnFiltersState> | any,
    loader?: boolean;
    fixedColumn?: boolean;
    withoutURLFilter?: boolean;
    noDataCentered?: boolean;
    inlineLoader?: boolean;
    onScrollBottom?: () => void;
    onRowClick?: (row: T) => void;
}
const DataTable = <T extends object>({
    data,
    columns,
    className = '',
    onPageChange,
    currentPage,
    TotalCount,
    showNavigation = true,
    TotalLength,
    onPerPageChange,
    perPageCount,
    enableColumnFilters = true,
    onColumnFiltersChange,
    loader = false,
    fixedColumn,
    withoutURLFilter,
    noDataCentered,
    inlineLoader = false,
    onScrollBottom,
    onRowClick,
}: DataTableProps<T>) => {

    const commonPadding = localStorage.getItem("tPadding") || '13px';
    const table = useReactTable({
        data: data,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        enableColumnFilters: enableColumnFilters,
        pageCount: TotalLength / perPageCount,
        manualPagination: true,
        enableFilters: true,
        enableGlobalFilter: true,
        getFilteredRowModel: getFilteredRowModel(),
        onColumnFiltersChange: onColumnFiltersChange,
    });
    const [colorMode] = useColorMode();
    const thBackground = colorMode === 'light' ? '#f7efe5' : '#374358';
    const thColor = colorMode === 'light' ? 'black' : '#dadada';


    return (
        <div className="table-responsive">
            <Box as="div" sx={{ paddingLeft: '15px', paddingRight: '15px', paddingBottom: '10px' }}>
                <div 
                    className={`${fixedColumn ? "tableNotFixHead" : "tableFixHead"}`} 
                    style={{ borderRadius: 10, border: `1px solid ${theme?.colors?.muted}` }}
                    onScroll={(e) => {
                        const target = e.target as HTMLDivElement;
                        // Add a 50px threshold to ensure it fires reliably when reaching the bottom
                        if (target.scrollHeight - target.scrollTop <= target.clientHeight + 50) {
                            if (onScrollBottom) {
                                onScrollBottom();
                            }
                        }
                    }}
                >
                    <table className={`${className + TabelClassname?.PrimaryTabel} mb-0 table-hover`}>
                        <thead className="position-relative">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header: any, index) => {
                                        const isCommentsHeader = header.column.columnDef.header === "Comments";
                                        const isNoColumn = header.column.columnDef.header === "No";

                                        let specificClass = '';
                                        if (header.column.columnDef.header === "No. of Tasks" || header.column.columnDef.header === "Spent Time" || header.column.columnDef.header === "Todo Checklist" || header.column.columnDef.header === "Is Completed" || header.column.columnDef.header === "Completion Ring" || header.column.columnDef.header === "Delete") {
                                            specificClass = 'th-no-of-tasks';
                                        } else if (["Quote Number", "Estimate Time", "Project Name", "Remaining Time", "Stages Name List", "Project Worker", "Architect", "TM Worker", "Status", "Stage Name", "TM Manager", "Stage Due Date", "Comments", "Highlighters", "promise", "Todo Name", "Todo Assignee", "Todo Checklist", "Due Date", "CheckList Items", "Color"].includes(header.column.columnDef.header)) {
                                            specificClass = 'th-quote-number';
                                        }
                                        return (
                                            <th className={`th align-middle border-top-0 border-bottom-0 fixed-column-${index} ${specificClass}${isNoColumn ? 'th-no-column' : ''}`}
                                                key={index}
                                                style={{ backgroundColor: thBackground, padding: `${commonPadding}px`, fontSize: 14, color: thColor }}>

                                                {header.column.getCanFilter() &&
                                                    <FilterPopup column={header.column.columnDef} id={`column${index}`} withoutURLFilter={withoutURLFilter} />}
                                                <Text variant='Poppins16Normal25lh' sx={{ color: colorMode === "light" ? "black" : "#dadada" }} as={"span"} className="text-center position-relative">
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {isCommentsHeader && <CommentPopup />}
                                                </Text>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}

                        </thead>
                        <tbody>
                            {(!loader || inlineLoader) && (
                                table.getRowModel().rows.map((row, rowIndex) => (
                                     <tr key={rowIndex} data-state={row.getIsSelected() && "selected"}
                                         className={`table-hover-row ${colorMode === "light" ? "light-mode" : "dark-mode"}`}

                                         style={{
                                             overflow: 'hidden',

                                             borderBottom:
                                                 (rowIndex + 1) === table.getRowModel().rows?.length ? 'none' :
                                                     '1px solid #D3D5D9',
                                         }}
                                     >
                                        {row.getVisibleCells().map((cell, cellIndex) => {
                                            const hasOwnColor =
                                                cell.column.columnDef.header === "Stages Name List" || cell.column.columnDef.header === "Highlighters";
                                            return (
                                                <td className={"fixed-column-" + cellIndex + " table-cell"} key={cell.id} style={{
                                                    padding: `${commonPadding}px`, display: "gird",
                                                    placeContent: "center", background: colorMode === "light" ? "#fff" : "#282e42",
                                                    ...(hasOwnColor
                                                        ? {} // DO NOT set color → let inner component decide
                                                        : {
                                                            color: colorMode === "light" ? "#0a0a0a" : "",
                                                        }),
                                                }}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>

                                            )
                                        })}
                                    </tr>
                                ))
                            )}

                            {loader && inlineLoader && (
                                <tr>
                                    <td colSpan={columns.length} style={{ textAlign: 'center', padding: '10px', background: 'none' }}>
                                        <Spinner />
                                    </td>
                                </tr>
                            )}

                            {!loader && data?.length === 0 && noDataCentered && (
                                <tr>
                                    <td colSpan={columns.length} style={{ textAlign: "center", padding: "30px", color: thColor, background: 'none' }}>
                                        No Data Found!
                                    </td>
                                </tr>
                            )}
                        </tbody>

                    </table>
                    {loader && !inlineLoader && (
                        <Spinner sx={{ position: "absolute", top: "50%", left: "50%" }} />
                    )}
                    {!loader && data?.length === 0 && !noDataCentered && (
                        <Text sx={{ position: "absolute", top: "50%", left: "50%", color: thColor }} >No Data Found!</Text>
                    )}
                </div>
            </Box >
            {
                showNavigation ?
                    <Box className="sticky-navigation">
                        < TablePagination
                            // className="sticky-navigation"
                            onPageChange={(page) => { onPageChange(page) }}
                            onPerPageChange={onPerPageChange}
                            TotalCount={TotalCount}
                            perPageCount={perPageCount}
                            TotalLength={TotalLength}
                            CurrentPage={currentPage}

                        /></Box > : null}
        </div >
    );
};

export default DataTable;
